import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Card, DatePicker, Empty, Image, Input, List, message as antdMessage, Modal, Pagination, Popconfirm, Space, Spin, Tabs, Typography, Upload, Form } from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { apiClient } from '../contexts/AuthContext';

const { Text, Paragraph } = Typography;

// 资讯发布页面
const BulletinBoard = ({ user }) => {
  const [tabs, setTabs] = useState([]); // { key, label, raw }
  const [activeTabKey, setActiveTabKey] = useState(null);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [tabsError, setTabsError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalForm] = Form.useForm();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [currentSeq, setCurrentSeq] = useState(null); // 用于区分新增和修改模式

  const localUser = useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('parse user from localStorage failed', e);
      return null;
    }
  }, []);

  const customerId = localUser?.customerId || 'S00001'; // 备用值

  // 加载栏目(tab) 配置
  useEffect(() => {
    const fetchTabs = async () => {
      setTabsLoading(true);
      setTabsError(null);
      try {
        const res = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
          tableName: 'BULLETIN_BOARD',
          colName: 'BULLETIN_BOARD'
        });

        const raw = res?.data?.data;
        let parsedTabs = [];

        if (Array.isArray(raw)) {
          // 有些实现可能已经是数组
          parsedTabs = raw;
        } else if (typeof raw === 'string') {
          // 题目返回 data 是字符串形式的 JSON 数组
          try {
            parsedTabs = JSON.parse(raw);
          } catch (e) {
            console.error('parse enum data error', e);
          }
        }

        const finalTabs = (parsedTabs || [])
          .filter(item => item && item.enumCode && item.enumName)
          .sort((a, b) => (a.showSort || 0) - (b.showSort || 0))
          .map(item => ({
            key: item.enumCode,
            label: item.enumName,
            raw: item
          }));

        setTabs(finalTabs);
        if (!activeTabKey && finalTabs.length > 0) {
          setActiveTabKey(finalTabs[0].key);
        }
      } catch (err) {
        console.error('fetch enum tabs failed', err);
        setTabsError('获取栏目配置失败');
        antdMessage.error('获取栏目配置失败');
      } finally {
        setTabsLoading(false);
      }
    };

    fetchTabs();
  }, []);

  // 加载当前 tab 的消息列表
  useEffect(() => {
    const fetchMessages = async () => {
      if (!activeTabKey || !customerId) return;

      setListLoading(true);
      setListError(null);
      try {
        const body = {
          customerId,
          tag: activeTabKey,
          pageSize,
          pageNo
        };

        const res = await apiClient.post('/sports-server-prod/taller.api.v1/message/pageList', body);
        const data = res?.data?.data || {};
        const list = data.list || data.records || [];
        const totalRecord = data.totalRecord || data.total || list.length;

        // 更新列表数据，使用picUrl作为首页图片
        const updatedList = list.map(item => ({
          ...item,
          fileName: item.picUrl ? [item.picUrl] : item.fileName || []
        }));
        
        setMessages(updatedList);
        setTotal(totalRecord);
      } catch (err) {
        console.error('fetch messages failed', err);
        setListError('获取消息列表失败');
        antdMessage.error('获取消息列表失败');
      } finally {
        setListLoading(false);
      }
    };

    fetchMessages();
  }, [activeTabKey, pageNo, pageSize, customerId, refreshTrigger]);

  const handleTabChange = (key) => {
    setActiveTabKey(key);
    setPageNo(1);
  };

  const handlePageChange = (page, size) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleAddClick = () => {
    modalForm.resetFields();
    setUploadedImageUrl('');
    setCurrentSeq(null); // 新增模式
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleModalOk = async () => {
    try {
      const values = await modalForm.validateFields();
      
      if (!uploadedImageUrl) {
        antdMessage.error('请上传首页图片');
        return;
      }

      // 如果是修改模式，先调用删除接口
      if (currentSeq) {
        const deleteRes = await apiClient.get(`/sports-base-server-prod/babyGroup/delete?customerId=${customerId}&seq=${currentSeq}`);
        if (deleteRes.data.result !== '1') {
          antdMessage.error('修改失败：删除原数据失败');
          return;
        }
      }

      const payload = {
        customerId: localUser?.customerId || 'S00001',   //当前localstorage.customerId
        userName: localUser?.name || '未知用户', //当前localstorage.user.name
        openId: localUser?.name || '未知用户', //当前localstorage.user.name
        fileName: [uploadedImageUrl],   // 首页图片
        context: values.title,  // 标题
        position: '', //固定
        type: 4, //固定
        tag: activeTabKey, //当前tab的enumCode
        classId: localUser?.classId || '', //当前localstorage.user.classId
        externalLinkUrl: values.externalLinkUrl, // 公众号链接
      };

      const res = await apiClient.post('/sports-base-server-prod/babyGroup/add', payload);
      
      if (res.data.result === '1') {
        antdMessage.success('保存成功');
        setIsModalVisible(false);
        setRefreshTrigger(prev => prev + 1);
        setCurrentSeq(null); // 重置seq状态
      } else {
        antdMessage.error('保存失败');
      }
    } catch (error) {
      console.error('save bulletin failed', error);
      antdMessage.error('保存失败');
    }
  };

  // 图片上传处理
  const uploadProps = {
    name: 'file',
    accept: 'image/*',
    maxCount: 1,
    showUploadList: false,
    customRequest: async (options) => {
      const { file, onSuccess, onError } = options;
      const formData = new FormData();
      formData.append('file', file);

      try {
        setUploadingImage(true);
        const response = await apiClient.post('/sports-server-prod/sys.api.v1/file/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        onSuccess(response.data);
      } catch (error) {
        onError(error);
      } finally {
        setUploadingImage(false);
      }
    },
    onChange(info) {
      if (info.file.status === 'done') {
        const data = info.file.response.data;
        const key = Object.keys(data)[0];
        const value = data[key];
        setUploadedImageUrl(value);
        antdMessage.success('上传成功');
      } else if (info.file.status === 'error') {
        antdMessage.error('上传失败');
      }
    },
  };

  const handleDelete = async (seq) => {
    try {
      const res = await apiClient.get(`/sports-base-server-prod/babyGroup/delete?customerId=${customerId}&seq=${seq}`);
      if (res.data.result === '1') {
        antdMessage.success('删除成功');
        setRefreshTrigger(prev => prev + 1);
      } else {
        antdMessage.error('删除失败');
      }
    } catch (err) {
      console.error('delete bulletin failed', err);
      antdMessage.error('删除失败');
    }
  };

  const renderItem = (item) => {
    const seq = item.seq;

    const handleEdit = () => {
      modalForm.setFieldsValue({
        title: item.context,
        externalLinkUrl: item.externalLinkUrl,
      });
      setUploadedImageUrl(item.fileName && item.fileName[0] ? item.fileName[0] : '');
      setCurrentSeq(item.seq); // 设置为修改模式，传入seq
      setIsModalVisible(true);
    };

    return (
      <Card
        key={seq}
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* 首页图片 */}
          {(item.fileName && item.fileName[0]) && (
            <Image
              src={item.fileName[0]}
              alt="首页图片"
              width={60}
              height={60}
              style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
            />
          )}
          
          {/* 标题、发布人、发布时间和公众号链接 */}
          <div style={{ flex: 1 }}>
            <div>
              <Text strong style={{ fontSize: 14 }}>{item.context || '无标题'}</Text>
            </div>
            <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item.registUser ? `发布人: ${item.registUser}` : ''}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item.registDate ? `发布时间: ${item.registDate}` : ''}
              </Text>
            </div>
            {item.externalLinkUrl && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>公众号链接：</Text>
                <a href={item.externalLinkUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                  {item.externalLinkUrl}
                </a>
              </div>
            )}
          </div>
          
          {/* 操作按钮 */}
          <div style={{ flexShrink: 0 }}>
            <Space>
              <Button type="link" size="small" onClick={handleEdit}>修改</Button>
              <Popconfirm
                title="确认删除"
                description="确定要删除这条资讯吗？"
                onConfirm={() => handleDelete(item.seq)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger size="small">删除</Button>
              </Popconfirm>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  const tabItems = tabs.map(t => ({ key: t.key, label: t.label }));

  return (
    <div style={{ padding: 16 }}>
      <Card bodyStyle={{ padding: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              资讯发布
            </Typography.Title>
          </div>

          <div>
            {tabsLoading ? (
              <Spin />
            ) : (
              <Tabs
                items={tabItems}
                activeKey={activeTabKey || (tabItems[0] && tabItems[0].key)}
                onChange={handleTabChange}
                tabBarGutter={16}
                tabBarExtraContent={
                  <Space>
                    <Button onClick={() => setRefreshTrigger(prev => prev + 1)}>
                      刷新
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddClick}>
                      新增发布
                    </Button>
                  </Space>
                }
              />
            )}
            {tabsError && (
              <Text type="danger" style={{ marginTop: 8 }}>
                {tabsError}
              </Text>
            )}
          </div>

          <div>
            <Spin spinning={listLoading}>
              {listError && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="danger">{listError}</Text>
                </div>
              )}
              {(!messages || messages.length === 0) && !listLoading ? (
                <Empty description="暂无数据" />
              ) : (
                <List
                  dataSource={messages}
                  renderItem={renderItem}
                />
              )}
            </Spin>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              current={pageNo}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showTotal={(t) => `共 ${t} 条`}
            />
          </div>
        </Space>
      </Card>

      {/* 新增发布弹窗 */}
      <Modal
        title={`${currentSeq ? '修改' : '新增'}${tabs.find(tab => tab.key === activeTabKey)?.label || '发布'}`}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={modalForm}
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入标题" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="externalLinkUrl"
            label="公众号链接"
            rules={[{ required: true, message: '请输入公众号链接' }]}
          >
            <Input placeholder="请输入公众号链接" />
          </Form.Item>

          <Form.Item
            label="首页图片"
            rules={[{ required: true, message: '请上传首页图片' }]}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} loading={uploadingImage}>
                {uploadedImageUrl ? '重新上传' : '上传图片'}
              </Button>
            </Upload>
            {uploadedImageUrl && (
              <div style={{ marginTop: 8 }}>
                <Image
                  src={uploadedImageUrl}
                  alt="已上传图片"
                  width={120}
                  height={120}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BulletinBoard;